import { useCallback } from "react";
import { useDispatch, useStore } from "react-redux";
import { RootState, useAppSelector } from "src/store";
import { useContactsApi } from "../api";
import { resetError, resetList, setError, setFilter, setList, setLoading } from "../slices";
import { GetContactsApiParams, GetContactsParams } from "../types";

const getServerSortField = (sort?: string) => {
  if (!sort) return undefined;

  const sortMap: Record<string, string> = {
    phoneNumber: "phone_number",
    validStatus: "valid_status",
    optInDate: "opt_in_date",
    optInType: "opt_in_type",
    optOutDate: "opt_out_date",
    optOutType: "opt_out_type",
    createdBy: "created_by",
    createdAt: "date_created",
    updatedAt: "date_updated",
    dateAdded: "date_added",
    timeZone: "time_zone",
  };

  const separatorIndex = sort.lastIndexOf("_");
  const field = sort.slice(0, separatorIndex);
  const order = sort.slice(separatorIndex + 1);
  const serverField = sortMap[field] || field;
  return `${serverField}_${order}`;
};

export const buildGetContactsParams = (
  companyId: string,
  filter: GetContactsParams,
): GetContactsApiParams => ({
  companyId,
  limit: filter.limit,
  offset: filter.offset,

  query: filter.query || undefined,
  sort: filter.sortMetadataId ? undefined : getServerSortField(filter.sort),
  sortMetadataId: filter.sortMetadataId || undefined,
  sortOrder: filter.sortMetadataId ? filter.sortOrder || 'asc' : undefined,
  queryMetadataIds: filter.query && filter.queryMetadataIds?.length
    ? filter.queryMetadataIds.join(',')
    : undefined,
  groups: filter.groups || undefined,
  notInGroups: filter.notInGroups || undefined,
  validStatus: filter.validStatus || undefined,
  isSpam: filter.isSpam || undefined,
});

export const useContactsService = () => {
  const dispatch = useDispatch();
  const { getState } = useStore<RootState>();
  const { loading, filter, list, error } = useAppSelector((state) => state.contacts);

  const api = useContactsApi();

  const getContacts = useCallback(async (companyId: string) => {
    const filter = getState().contacts.filter;

    try {
      dispatch(resetError());
      dispatch(setLoading({ getList: true }));

      const response = await api.getContacts(buildGetContactsParams(companyId, filter));
      dispatch(setList(response.data));

      return response.data;
    } catch (e) {
      dispatch(setError(e));
      throw e;
    } finally {
      dispatch(setLoading({ getList: false }));
    }
  }, []);

  const changeContactsFilter = useCallback((filter: Partial<RootState["contacts"]["filter"]>) => {
    dispatch(setFilter(filter));
  }, []);

  const resetContacts = useCallback(() => {
    dispatch(resetList());
  }, []);

  return {
    loading,
    filter,
    list,
    error,
    getContacts,
    changeContactsFilter,
    resetContacts,
  };
};
